import React, { useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../utils/constants';
import styles from '../../styles/Callback.module.scss';

const INITIAL = { phone: '', name: '', comment: '' };

const isValidPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

const CallbackModal = ({ open, onClose }) => {
  const titleId = useId();
  const phoneRef = useRef(null);
  const [values, setValues] = useState(INITIAL);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => phoneRef.current?.focus(), 50);

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, loading]);

  useEffect(() => {
    if (!open) {
      setValues(INITIAL);
      setError('');
      setSuccess('');
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const handleChange = ({ target: { name, value } }) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !loading) onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phone = values.phone.trim();
    if (!isValidPhone(phone)) {
      setError('Вкажіть коректний номер телефону');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await axios.post(`${BASE_URL}/callback`, {
        phone,
        name: values.name.trim(),
        comment: values.comment.trim(),
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      });
      setSuccess(data?.message || 'Заявку прийнято. Ми зателефонуємо найближчим часом.');
      setValues(INITIAL);
      window.setTimeout(() => onClose(), 1800);
    } catch (err) {
      setError(
        err.response?.data?.message
          || 'Не вдалося надіслати заявку. Спробуйте пізніше або зателефонуйте нам.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Закрити"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className={styles.header}>
          <span className={styles.iconWrap} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </span>
          <h2 id={titleId} className={styles.title}>Перетелефонуйте мені</h2>
          <p className={styles.subtitle}>
            Залиште номер — ми зателефонуємо в робочий час
          </p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>
              Телефон <span className={styles.required}>*</span>
            </span>
            <input
              ref={phoneRef}
              className={styles.input}
              type="tel"
              name="phone"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+38 (0XX) XXX-XX-XX"
              value={values.phone}
              onChange={handleChange}
              disabled={loading || Boolean(success)}
              required
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Ім&apos;я</span>
            <input
              className={styles.input}
              type="text"
              name="name"
              autoComplete="name"
              placeholder="Як до вас звертатися"
              value={values.name}
              onChange={handleChange}
              disabled={loading || Boolean(success)}
              maxLength={80}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Коментар</span>
            <textarea
              className={styles.textarea}
              name="comment"
              placeholder="Про що зателефонувати (необов'язково)"
              value={values.comment}
              onChange={handleChange}
              disabled={loading || Boolean(success)}
              maxLength={500}
              rows={3}
            />
          </label>

          {error && <p className={styles.error} role="alert">{error}</p>}
          {success && <p className={styles.success} role="status">{success}</p>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancel}
              onClick={onClose}
              disabled={loading}
            >
              Скасувати
            </button>
            <button
              type="submit"
              className={styles.submit}
              disabled={loading || Boolean(success)}
            >
              {loading ? 'Надсилаємо…' : 'Зателефонуйте мені'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CallbackModal;
